import * as Sentry from '@sentry/nextjs';
import { PrivyProvider } from '@privy-io/react-auth';
import { WagmiProvider } from '@privy-io/wagmi';
import { DehydratedState, HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { SpeedInsights } from '@vercel/speed-insights/next';
import Decimal from 'decimal.js-light';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { defaultConfig } from 'tailwind-variants';

import { ErrorBoundary } from '@/components/App/ErrorBoundary';
import { AppToaster } from '@/components/AppToaster';
import { AuthProvider } from '@/components/Auth/AuthProvider';
import { WalletConnectionModalProvider } from '@/components/Auth/WalletConnectionModalProvider';
import { FeatureFlagsProvider } from '@/components/FeatureFlags/FeatureFlagsProvider';
import { DarkmodeProvider } from '@/components/Header/DarkmodeToggle';
import { PromoCarousel } from '@/components/PromoCarousel';

import { useFxRates } from '@/hooks/useFxRates';
import { useGeoCheck } from '@/hooks/useGeoCheck';
import { useNodeProviderToken } from '@/hooks/useNodeProviderToken';

import { referrersStoreSelectors, useReferrerStore } from '@/store/referrals/';

import { Fonts } from '@/ui/Fonts';

import { identifyTheme, initializeAmpli } from '@/utils/amplitude';
import type { CachedWalletAddresses } from '@/utils/cachedWalletAddresses';
import type { FeatureFlagValues } from '@/utils/featureFlags';
import { primitiveColors } from '@/utils/generated/primitiveColors';
import { waitForLedgerProvider } from '@/utils/ledger';
import { PRIVY_CLIENT_ID, PRIVY_ID, config as privyConfig } from '@/utils/privy';
import { initializePylon } from '@/utils/pylon';
import { makeQueryClient } from '@/utils/queryClient';
import { initRedditPixel } from '@/utils/reddit';
import { isMarketingPage } from '@/utils/routes';
import { config as wagmiConfig } from '@/utils/wagmi';

import { DEFAULT_SEO_CONFIG } from '../../next-seo.config';
// patch ref: https://github.com/facebook/react/issues/11538#issuecomment-417504600
import '../../node-patch';
import { SankeyProvider } from '../components/SankeyDiagram/SankeyProvider';
import { twMergeConfig } from '../constants/twMergeConfig';
import '../styles/tailwind.css';
import '../styles/vars.css';
import { classNames } from '../utils/classnames';

// client-only components
const AppUpdater = dynamic(
  () => import('@/components/Updater/AppUpdater').then(({ AppUpdater }) => AppUpdater),
  { ssr: false },
);
const TransactionUpdater = dynamic(
  () =>
    import('@/components/Updater/TransactionUpdater').then(
      ({ TransactionUpdaterManager }) => TransactionUpdaterManager,
    ),
  { ssr: false },
);
const SolanaTransactionUpdater = dynamic(
  () =>
    import('@/components/Updater/SolanaTransactionUpdater').then(
      ({ SolanaTransactionUpdater }) => SolanaTransactionUpdater,
    ),
  { ssr: false },
);
if (process.env.NODE_ENV !== 'test' && typeof window !== 'undefined') {
  initializeAmpli();
  identifyTheme();
  initRedditPixel();
  initializePylon();

  // Reload the page once on chunk load failures from stale deployments.
  // Uses sessionStorage to persist the "already reloaded" flag across the reload.
  // If sessionStorage is unavailable (Safari private mode), skip the reload
  // entirely to avoid an infinite loop since there's no way to persist the flag.
  // Only enabled in production to avoid masking real errors during development.
  if (process.env.NODE_ENV === 'production') {
    const handleChunkError = (message: string) => {
      if (!message.includes('Failed to load chunk') && !message.includes('Loading chunk')) return;

      const key = 'matcha:chunk-reload';
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
        window.location.reload();
      } catch {
        // sessionStorage unavailable — skip reload to avoid infinite loop
      }
    };

    window.addEventListener('error', (event) => handleChunkError(event.message || ''));
    window.addEventListener('unhandledrejection', (event) =>
      handleChunkError(event.reason?.message || String(event.reason || '')),
    );
  }

  // Await discovery before hydration without changing the server-rendered provider tree.
  await waitForLedgerProvider();
}

defaultConfig.twMergeConfig = twMergeConfig;

/**
 * App-level React Query hooks that fetch/seed global state at startup.
 * Rendered inside `QueryClientProvider` (see below) so the hooks can access
 * the QueryClient via context; calling them directly in `MatchaApp` would
 * sit above the provider and throw.
 */
function AppQueries() {
  useNodeProviderToken();
  useGeoCheck();
  useFxRates();
  return null;
}

export type MatchaAppProps = {
  cachedWalletAddresses?: CachedWalletAddresses | null;
  dehydratedState?: DehydratedState;
  featureFlags?: FeatureFlagValues;
};

function MatchaApp({ Component, pageProps }: AppProps<MatchaAppProps>) {
  const router = useRouter();
  const wagmiConfigChains = wagmiConfig.chains.reduce<Record<string, number>>((acc, curr) => {
    acc[curr.name] = curr.id;
    return acc;
  }, {});
  Sentry.setContext(
    `Wagmi Configured Chains ${typeof window === 'undefined' ? 'server' : 'client'}`,
    wagmiConfigChains,
  );

  const hasContainerHomeStyles = isMarketingPage(router.pathname);

  // referrals
  const { ref } = router.query;
  const { setReferrerId, referrerId, isExpired } = useReferrerStore(referrersStoreSelectors);
  const [queryClient] = useState(() => makeQueryClient());

  useEffect(() => {
    if (ref && (referrerId !== ref || isExpired())) {
      setReferrerId(ref.toString());
    }
  }, [ref, referrerId, setReferrerId, isExpired]);

  useEffect(() => {
    // Ensure decimal-js settings are correct on mount
    Decimal.set({ precision: 80, toExpPos: 1000, toExpNeg: -1000, rounding: Decimal.ROUND_DOWN });
  }, []);

  const [preferredColorScheme] = useState<'light' | 'dark'>(() => {
    return typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  return (
    <>
      <DefaultSeo {...DEFAULT_SEO_CONFIG} />
      <Fonts />
      <ErrorBoundary>
        <DarkmodeProvider>
          {(theme) => (
            <SankeyProvider>
              <PrivyProvider
                appId={PRIVY_ID}
                clientId={PRIVY_CLIENT_ID}
                config={{
                  ...privyConfig,
                  appearance: {
                    ...privyConfig.appearance,
                    theme:
                      preferredColorScheme === 'dark' || theme === 'dark'
                        ? (primitiveColors['gray-1300'] as `#${string}`)
                        : 'light',
                  },
                }}
              >
                <QueryClientProvider client={queryClient}>
                  <HydrationBoundary state={pageProps.dehydratedState}>
                    <WagmiProvider config={wagmiConfig}>
                      <AuthProvider cachedWalletAddresses={pageProps.cachedWalletAddresses}>
                        <WalletConnectionModalProvider>
                          <FeatureFlagsProvider flags={pageProps.featureFlags}>
                            <div
                              className={classNames(
                                'font-body min-h-screen relative overflow-x-clip',
                                hasContainerHomeStyles
                                  ? 'bg-special-background-homepage'
                                  : '[background:var(--token-gradient-app-background)] app-bg-gradient-viewport',
                              )}
                              id="app"
                            >
                              <Component {...pageProps} />
                              <PromoCarousel />
                              <AppQueries />
                              <AppUpdater />
                              <AppToaster />
                            </div>
                            <TransactionUpdater />
                            <SolanaTransactionUpdater />
                          </FeatureFlagsProvider>
                        </WalletConnectionModalProvider>
                      </AuthProvider>
                    </WagmiProvider>
                  </HydrationBoundary>
                </QueryClientProvider>
              </PrivyProvider>
            </SankeyProvider>
          )}
        </DarkmodeProvider>
      </ErrorBoundary>
      <SpeedInsights sampleRate={0.15} />
    </>
  );
}

export default MatchaApp;
